import React from 'react';
import type { NextPage } from 'next';
import Link from 'next/link';
import styled from '@emotion/styled';
import {
  Box,
  Text,
  Flex,
  ButtonLink,
  Image,
  SpinnerFullPage,
  TextLink,
  Palette,
  ColorKey,
  Theme,
} from '@updater/ui-uds';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { useTrackViewOnMount } from 'utils/use-track-on-mount';
import { useIsHomeOwner } from 'hooks/useIsHomeOwner';
import { useFeature } from 'context/growthbook/useFeature';

import { PageTemplate } from 'components/page-template';
import { ProgressBarCard } from 'flows/core/components/cards/progress-bar-card/ProgressBarCard';
import { useProgressBarData } from 'flows/core/hooks/use-progress-bar-data';
import { useDollyEligibility } from '../hooks/use-dolly-eligibility';
import { useItemEligibility } from '../hooks/use-item-eligibility';
import { useEasyTasks } from '../hooks/use-daisy-chain';
import { useUserData } from '../hooks/use-profile';
import { IntegratedChat } from 'flows/virtual-building-assistant/components/chat/integrated-chat/IntegratedChat';
import { ResponsiveValue } from 'styled-system';

const CustomProgressBarContainer = styled(Box)`
  max-width: 536px;
  width: 100%;
  padding-left: 0;
  padding-right: 0;
  margin-top: 0rem;
  margin-bottom: 1.5rem;
`;

export const Welcome: NextPage = () => {
  const { hasItemEligibility: tviEligible } = useItemEligibility('tv_internet');
  const isHomeOwner = useIsHomeOwner();
  const { easyTasks } = useEasyTasks();
  const userInfo = useUserData();

  // Feature flag for progress bar
  const { on: showProgressBar } = useFeature(
    'consumer_app_use_well_done_progress_bar'
  );

  // Feature flag for Virtual Building Assistant
  const vbaFeature = useFeature('consumer_app_use_upo_vba');
  const showVBA = (vbaFeature as { on: boolean }).on;

  // Progress bar data
  const {
    priorityCards,
    completedCards,
    validatingCards,
    currentMoveDate,
    showProgressBar: showProgressBarData,
  } = useProgressBarData();

  // Only show progress bar if both feature flag and data conditions are met
  const shouldShowProgressBar = showProgressBar && showProgressBarData;

  useDollyEligibility();

  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'sign-up',
    pageName: 'welcome',
  });

  useTrackViewOnMount(trackEvent, { object: 'welcome' });

  if (isHomeOwner === 'loading') {
    return <SpinnerFullPage />;
  }

  const firstEasyTask = easyTasks?.length > 0 ? easyTasks[0] : undefined;

  return (
    <PageTemplate maxWidth={536} hasHeaderMenu={false}>
      {shouldShowProgressBar && (
        <CustomProgressBarContainer>
          <ProgressBarCard
            priorityCards={priorityCards}
            completedCards={completedCards}
            validatingCards={validatingCards}
            currentMoveDate={currentMoveDate}
          />
        </CustomProgressBarContainer>
      )}
      <Box textAlign="center">
        <Text as="h2" variant="lBold">
          {userInfo?.user?.firstName}, welcome!
        </Text>
        <Text marginTop="xs" as="p" variant="m">
          There are two ways to get moving – tackle the easy, required tasks
          first, or see your full list and go at your own pace.
        </Text>
      </Box>

      {/* Card */}
      <Box
        width="100%"
        marginTop="m"
        border="1px solid"
        borderColor="border"
        borderRadius="s"
        overflow="hidden"
      >
        <Flex
          backgroundColor={
            Palette.blue200 as ResponsiveValue<ColorKey, Theme> | undefined
          }
          minHeight="9.75rem"
        >
          <Image
            margin="auto"
            display="block"
            src="/images/welcome/mascot-checklist.svg"
          />
        </Flex>

        <Box paddingX="s" paddingBottom="s">
          <Box marginTop="s">
            <Text variant="s">
              I want to get the easy stuff out of the way first
            </Text>
            <Text marginTop="s" color="textSoft" variant="s">
              Time to complete
              <Text display="block" variant="sBold" as="span">
                5 min
              </Text>
            </Text>
          </Box>

          <Link href={firstEasyTask?.route ?? '/'} passHref>
            <ButtonLink
              data-testid="button-link"
              marginTop="xs"
              isFluidWidth
              onClick={() =>
                trackEvent({
                  verb: 'clicked',
                  object: 'from-welcome-to-easy-task',
                  details: {
                    route: firstEasyTask?.route,
                    tviEligible,
                  },
                })
              }
            >
              Let’s do it
            </ButtonLink>
          </Link>
        </Box>
      </Box>

      <Box marginTop="l" textAlign="center">
        <Link passHref href="/">
          <TextLink
            variant="sBold"
            data-testid="link-skip-to-essentials"
            onClick={() =>
              trackEvent({
                verb: 'clicked',
                object: 'from-welcome-to-essentials',
                details: {
                  route: firstEasyTask?.route,
                  tviEligible,
                },
              })
            }
          >
            Go at my own pace
          </TextLink>
        </Link>
      </Box>

      {showVBA && (
        <Box textAlign="center" marginTop="l">
          <Text variant="m" marginBottom="s">
            Need help? Ask our Virtual Building Assistant
          </Text>
          <IntegratedChat
            position="right"
            trackingDomain="welcome-page"
            trackingDetails={{ tviEligible }}
            onOpen={() => {
              trackEvent({
                verb: 'clicked',
                object: 'from-welcome-to-floating-vba',
                details: { tviEligible },
              });
            }}
            onClose={() => {
              trackEvent({
                verb: 'clicked',
                object: 'from-welcome-close-floating-vba',
                details: { tviEligible },
              });
            }}
          />
        </Box>
      )}
    </PageTemplate>
  );
};

export default Welcome;
